// WEB ONLY — uses react-router-dom, do not import from native code
/**
 * GLOBAL NOTIFICATION POLLING HOOK (Web-only)
 *
 * Polls for chat notification pills every 5 seconds from ANY page.
 * Dispatches 'coach-cedar-notification' custom events for the
 * InAppNotificationBannerManager (mounted globally in App.tsx).
 *
 * This solves the architectural gap where polling only ran inside Chat.tsx,
 * meaning users on MyPath, Settings, etc. never saw in-app banners.
 *
 * Architecture:
 * - This hook: polls backend, dispatches banner events, acknowledges pills, handles auto-navigate
 * - Chat.tsx: still handles pill-to-message conversion when on /chat (chat-specific rendering)
 * - InAppNotificationBannerManager: listens for events, renders banners (with ID de-dup)
 *
 * NOTE: This file uses react-router-dom intentionally — it is ONLY mounted inside
 * the web-only <Router> in App.tsx. It is never imported by native code.
 *
 * Date: March 29, 2026
 * Fix: Notification regression — banners never fired on non-chat pages
 */

import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCurrentUserId } from '../utils/userStorage';
import { getAuthHeaders } from '../utils/auth';
import { API_BASE_URL } from '../utils/api';
import {
  getNotificationTitle,
  getNotificationIcon,
  getBannerMessage,
  shouldShowBanner,
  shouldAutoNavigate,
} from '../utils/notificationHelpers';

/**
 * Global notification polling hook.
 * Mount once in the Layout component of App.tsx.
 *
 * Does NOT handle pill-to-message conversion (that stays in Chat.tsx).
 * Only fires banner events + auto-navigate + acknowledges pills on non-chat pages.
 */
export function useGlobalNotificationPolling() {
  const navigate = useNavigate();
  const location = useLocation();
  const consecutiveFailures = useRef(0);
  const isPollingRef = useRef(false);
  // Stable ref for navigate to avoid useEffect re-firing on every render
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  // Stable ref for current path
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    // Don't poll on chat page — Chat.tsx has its own poller that also handles pill rendering
    if (location.pathname === '/chat') return;

    const pollNotifications = async () => {
      // Guard against overlapping polls
      if (isPollingRef.current) return;
      isPollingRef.current = true;

      try {
        if (typeof window === 'undefined') return;

        const userId = getCurrentUserId();
        const authHeaders = await getAuthHeaders();

        if (!authHeaders.Authorization || !userId) return;

        const response = await fetch(
          `${API_BASE_URL}/api/notifications/chat-pills/${userId}`,
          { headers: authHeaders }
        );

        if (!response.ok) {
          consecutiveFailures.current++;
          if (consecutiveFailures.current >= 5) {
            console.error('[GlobalPoll] 5+ consecutive failures, status:', response.status);
          }
          return;
        }

        // Reset on success
        consecutiveFailures.current = 0;

        const data = await response.json();
        if (!data.notifications || data.notifications.length === 0) return;

        // Re-check path — user may have navigated to chat during the fetch
        if (pathnameRef.current === '/chat') return;

        const notificationsToAcknowledge: string[] = [];

        for (const notification of data.notifications) {
          const notifId = notification.id;
          if (!notifId) continue; // Skip malformed notifications without IDs
          const notifType = notification.type ?? notification.notification_type;

          // Auto-navigate (post_workout_debrief) — only when NOT on chat
          if (shouldAutoNavigate(notifType, false)) {
            navigateRef.current(
              `/chat?from=auto-notification&notificationId=${notifId}&notificationType=${notifType}&timestamp=${Date.now()}`
            );
            return; // stop processing — chat will load and handle remaining
          }

          // Banner — dispatch event for InAppNotificationBannerManager
          // The banner manager de-duplicates by ID, so even if Chat.tsx also
          // processed this notification, only one banner will appear.
          if (shouldShowBanner(notifType, false)) {
            const event = new CustomEvent('coach-cedar-notification', {
              detail: {
                id: notifId,
                title: getNotificationTitle(notifType),
                message: getBannerMessage(
                  notification.message || notification.body || notification.description || '',
                  100
                ),
                icon: getNotificationIcon(notifType),
                conversationId: notification.conversation_id,
                timestamp: Date.now(),
              },
            });
            window.dispatchEvent(event);
          }

          notificationsToAcknowledge.push(notifId);
        }

        // Acknowledge all processed notifications so the API stops returning them
        if (notificationsToAcknowledge.length > 0) {
          await Promise.allSettled(
            notificationsToAcknowledge.map((notifId) =>
              fetch(`${API_BASE_URL}/api/notifications/acknowledge`, {
                method: 'POST',
                headers: {
                  ...authHeaders,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  user_id: userId,
                  notification_id: notifId,
                  action: 'viewed',
                }),
              }).catch(() => {
                // Non-critical — will retry on next poll cycle
              })
            )
          );
        }
      } catch (error) {
        consecutiveFailures.current++;
        if (consecutiveFailures.current >= 5) {
          console.error('[GlobalPoll] 5+ consecutive failures:', error);
        }
      } finally {
        isPollingRef.current = false;
      }
    };

    // 🔧 M35B: Also poll background responses from non-chat pages.
    // When Cedar finishes a response while the user is on MyPath/Settings,
    // this fires a banner so the user knows Cedar replied. The response
    // stays in background_chat_responses until Chat.tsx mounts and marks it retrieved.
    const pollBackgroundResponses = async () => {
      try {
        const userId = getCurrentUserId();
        const authHeaders = await getAuthHeaders();
        if (!authHeaders.Authorization || !userId) return;
        // Re-check — user may have navigated to chat
        if (pathnameRef.current === '/chat') return;

        const res = await fetch(
          `${API_BASE_URL}/api/background-responses/${userId}`,
          { headers: authHeaders }
        );
        if (!res.ok) return;
        // 🔧 M35B FORENSIC FIX: Endpoint returns {responses: [...], ...}, not a raw array.
        // Array.isArray() on the object was always false — banners never fired.
        const data = await res.json();
        const responses = data.responses || data;
        if (!Array.isArray(responses) || responses.length === 0) return;
        // Re-check path
        if (pathnameRef.current === '/chat') return;

        // Fire a banner for each new background response
        for (const bgResp of responses) {
          const preview = (bgResp.text || bgResp.response?.text || '').slice(0, 80);
          const event = new CustomEvent('coach-cedar-notification', {
            detail: {
              id: `bg_${bgResp.task_id || bgResp.message_id || Date.now()}`,
              title: 'Cedar responded!',
              message: preview ? `${preview}...` : 'Your response is ready',
              icon: '💬',
              timestamp: Date.now(),
            },
          });
          window.dispatchEvent(event);
        }
        // Do NOT mark as retrieved — Chat.tsx will do that when it mounts and displays them
      } catch {
        // Non-critical — background responses will still be fetched when user opens chat
      }
    };

    // Initial poll + interval
    pollNotifications();
    pollBackgroundResponses();
    const interval = setInterval(pollNotifications, 5000);
    const bgInterval = setInterval(pollBackgroundResponses, 5000);

    return () => {
      clearInterval(interval);
      clearInterval(bgInterval);
    };
  }, [location.pathname]);
}

export default useGlobalNotificationPolling;
